import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MapPin, Truck, Clock, Phone, CheckCircle, ArrowRight, ChevronRight, Tent, Wrench, Leaf, Sparkles } from 'lucide-react';
import QuickQuoteWidget from '../components/QuickQuoteWidget';
import {useSeo} from '../seo/SeoProvider';

// Données des zones de livraison (Tier 2 SEO)
// Chaque zone regroupe 10-20 communes avec code postal + distance
export const zonesData = {
  'pays-de-caux': {
    name: 'Pays de Caux',
    region: 'Seine-Maritime',
    distance: '15 à 30 minutes',
    description: 'Le Pays de Caux, plateau crayeux emblématique de la Normandie, s\'étend entre la Seine et la mer. Avec ses villages pittoresques, ses falaises et son patrimoine rural, c\'est une zone d\'intervention majeure pour 3DLoc.',
    metaTitle: 'Location de Matériel Pays de Caux | 18 Communes | 3DLoc Normandie',
    metaDescription: 'Location de matériel dans le Pays de Caux : Goderville, Les Loges, Turretot, Criquetot-l\'Esneval, Étretat... Livraison rapide dans 18 communes. Devis gratuit 3DLoc.',
    featuredCities: ['Étretat', 'Goderville', 'Les Loges', 'Turretot'],
    localContent: 'Le Pays de Caux est le berceau historique de notre activité. Entre la Côte d\'Albâtre et l\'arrière-pays agricole, nous y intervenons depuis des années pour tout type d\'événements : mariages champêtres, kermesses de village, fêtes communales, travaux agricoles et rénovations. Notre connaissance du terrain et des spécificités locales (vents, sols, accès) nous permet d\'offrir un service adapté.',
    communes: [
      { name: 'Goderville', postal: '76110', distance: '25 min' },
      { name: 'Les Loges', postal: '76790', distance: '15 min' },
      { name: 'Turretot', postal: '76280', distance: '13 min' },
      { name: 'Criquetot-l\'Esneval', postal: '76280', distance: '13 min' },
      { name: 'Anglesqueville-l\'Esneval', postal: '76280', distance: '12 min' },
      { name: 'Beaurepaire', postal: '76280', distance: '13 min' },
      { name: 'Sainte-Marie-au-Bosc', postal: '76280', distance: '11 min' },
      { name: 'Villainville', postal: '76280', distance: '14 min' },
      { name: 'Hermeville', postal: '76280', distance: '18 min' },
      { name: 'Pierrefiques', postal: '76280', distance: '16 min' },
      { name: 'Cuverville', postal: '76280', distance: '17 min' },
      { name: 'Vergetot', postal: '76280', distance: '17 min' },
      { name: 'Fongueusemare', postal: '76280', distance: '21 min' },
      { name: 'Bénouville', postal: '76790', distance: '23 min' },
      { name: 'Bordeaux-Saint-Clair', postal: '76790', distance: '20 min' },
      { name: 'La Poterie-Cap-d\'Antifer', postal: '76280', distance: '12 min' },
      { name: 'Saint-Jouin-Bruneval', postal: '76280', distance: '7 min' },
      { name: 'Beuzeville-la-Grenier', postal: '76210', distance: '25 min' }
    ]
  },
  'agglo-le-havre': {
    name: 'Agglomération du Havre',
    region: 'Seine-Maritime',
    distance: '10 à 25 minutes',
    description: 'L\'agglomération havraise, cœur économique de la Seine-Maritime, rassemble plus de 250 000 habitants sur plusieurs dizaines de communes. 3DLoc y assure une livraison rapide pour particuliers et professionnels.',
    metaTitle: 'Location de Matériel Agglo Le Havre | 22 Communes | 3DLoc',
    metaDescription: 'Location de matériel dans l\'agglomération du Havre : Montivilliers, Harfleur, Sainte-Adresse, Gonfreville-l\'Orcher... Livraison 24h dans 22 communes. 3DLoc.',
    featuredCities: ['Le Havre', 'Montivilliers', 'Harfleur', 'Sainte-Adresse'],
    localContent: 'Notre dépôt à Cauville-sur-Mer fait partie de l\'agglomération du Havre, ce qui nous permet d\'assurer une livraison express (souvent le jour même) dans toutes les communes du territoire. Mariages, réceptions d\'entreprise, chantiers de rénovation ou événements associatifs : nous intervenons 7J/7 sur rendez-vous.',
    communes: [
      { name: 'Le Havre', postal: '76600', distance: '22 min' },
      { name: 'Le Havre', postal: '76610', distance: '16 min' },
      { name: 'Le Havre', postal: '76620', distance: '14 min' },
      { name: 'Montivilliers', postal: '76290', distance: '15 min' },
      { name: 'Harfleur', postal: '76700', distance: '20 min' },
      { name: 'Sainte-Adresse', postal: '76310', distance: '16 min' },
      { name: 'Gonfreville-l\'Orcher', postal: '76700', distance: '22 min' },
      { name: 'Gainneville', postal: '76700', distance: '22 min' },
      { name: 'Rogerville', postal: '76700', distance: '23 min' },
      { name: 'Saint-Laurent-de-Brèvedent', postal: '76700', distance: '21 min' },
      { name: 'Octeville-sur-Mer', postal: '76930', distance: '7 min' },
      { name: 'Cauville-sur-Mer', postal: '76930', distance: 'Base 3DLoc' },
      { name: 'Fontaine-la-Mallet', postal: '76290', distance: '10 min' },
      { name: 'Fontenay', postal: '76290', distance: '10 min' },
      { name: 'Mannevillette', postal: '76290', distance: '5 min' },
      { name: 'Manéglise', postal: '76133', distance: '19 min' },
      { name: 'Notre-Dame-du-Bec', postal: '76133', distance: '10 min' },
      { name: 'Saint-Martin-du-Bec', postal: '76133', distance: '10 min' },
      { name: 'Rolleville', postal: '76133', distance: '12 min' },
      { name: 'Épouville', postal: '76133', distance: '15 min' },
      { name: 'Heuqueville', postal: '76280', distance: '5 min' },
      { name: 'Gonneville-la-Mallet', postal: '76280', distance: '10 min' },
      { name: 'Saint-Martin-du-Manoir', postal: '76290', distance: '19 min' },
      { name: 'Angerville-l\'Orcher', postal: '76280', distance: '20 min' }
    ]
  },
  'vallee-de-seine': {
    name: 'Vallée de Seine & Estuaire',
    region: 'Seine-Maritime',
    distance: '25 à 45 minutes',
    description: 'La vallée de la Seine et l\'estuaire concentrent un tissu industriel dense et de nombreuses communes résidentielles. 3DLoc y livre matériel événementiel et outillage professionnel.',
    metaTitle: 'Location de Matériel Vallée de Seine | 21 Communes | 3DLoc',
    metaDescription: 'Location de matériel dans la vallée de Seine : Bolbec, Lillebonne, Port-Jérôme, Tancarville, Saint-Romain-de-Colbosc... Livraison 48h. Devis gratuit 3DLoc.',
    featuredCities: ['Bolbec', 'Lillebonne', 'Port-Jérôme-sur-Seine', 'Tancarville'],
    localContent: 'La zone industrielle de Port-Jérôme et ses environs représentent un marché important pour la location professionnelle. Entre événements d\'entreprise (séminaires, inaugurations), locations particulières (mariages, fêtes) et chantiers de BTP, nous fournissons un matériel adapté aux exigences de la zone.',
    communes: [
      { name: 'Bolbec', postal: '76210', distance: '30 min' },
      { name: 'Gruchet-le-Valasse', postal: '76210', distance: '30 min' },
      { name: 'Lillebonne', postal: '76170', distance: '35 min' },
      { name: 'Petiville', postal: '76170', distance: '40 min' },
      { name: 'Tancarville', postal: '76430', distance: '30 min' },
      { name: 'Port-Jérôme-sur-Seine', postal: '76700', distance: '35 min' },
      { name: 'Sandouville', postal: '76430', distance: '24 min' },
      { name: 'Saint-Vigor-d\'Ymonville', postal: '76430', distance: '29 min' },
      { name: 'Saint-Aubin-Routot', postal: '76430', distance: '26 min' },
      { name: 'Saint-Gilles-de-la-Neuville', postal: '76430', distance: '30 min' },
      { name: 'Saint-Romain-de-Colbosc', postal: '76430', distance: '30 min' },
      { name: 'Saint-Vincent-Cramesnil', postal: '76430', distance: '30 min' },
      { name: 'Les Trois-Pierres', postal: '76430', distance: '31 min' },
      { name: 'Oudalle', postal: '76430', distance: '31 min' },
      { name: 'Gommerville', postal: '76430', distance: '26 min' },
      { name: 'Étainhus', postal: '76430', distance: '23 min' },
      { name: 'Épretot', postal: '76430', distance: '24 min' },
      { name: 'Sainneville', postal: '76430', distance: '21 min' },
      { name: 'Graimbouville', postal: '76430', distance: '25 min' },
      { name: 'La Cerlangue', postal: '76430', distance: '34 min' },
      { name: 'La Remuée', postal: '76430', distance: '33 min' }
    ]
  },
  'cote-fleurie': {
    name: 'Côte Fleurie & Pays d\'Auge',
    region: 'Calvados & Eure',
    distance: '35 minutes à 1h',
    description: 'La Côte Fleurie et le Pays d\'Auge, destinations touristiques majeures de Normandie, accueillent chaque année des milliers d\'événements. 3DLoc y livre matériel événementiel et mobilier de prestige.',
    metaTitle: 'Location de Matériel Côte Fleurie & Pays d\'Auge | 3DLoc Calvados',
    metaDescription: 'Location de matériel sur la Côte Fleurie et le Pays d\'Auge : Honfleur, Deauville, Lisieux, Pont-Audemer... Chapiteaux, mobilier événementiel. Devis gratuit 3DLoc.',
    featuredCities: ['Honfleur', 'Deauville', 'Lisieux', 'Pont-Audemer'],
    localContent: 'De Honfleur à Deauville, en passant par Lisieux et les manoirs du Pays d\'Auge, cette zone est l\'une des plus demandées pour les mariages et événements haut de gamme. 3DLoc met à disposition des chapiteaux premium, du mobilier élégant et des équipements spécifiques pour sublimer vos réceptions dans ce cadre d\'exception.',
    communes: [
      { name: 'Honfleur', postal: '14600', distance: '35 min' },
      { name: 'Deauville', postal: '14800', distance: '45 min' },
      { name: 'Trouville-sur-Mer', postal: '14360', distance: '45 min' },
      { name: 'Equemauville', postal: '14600', distance: '35 min' },
      { name: 'Villerville', postal: '14113', distance: '40 min' },
      { name: 'Pennedepie', postal: '14600', distance: '35 min' },
      { name: 'Lisieux', postal: '14100', distance: '1h' },
      { name: 'Pont-Audemer', postal: '27500', distance: '40 min' },
      { name: 'Berville-sur-Mer', postal: '27210', distance: '40 min' },
      { name: 'Beuzeville', postal: '27210', distance: '40 min' },
      { name: 'Touques', postal: '14800', distance: '45 min' },
      { name: 'Saint-Arnoult', postal: '14800', distance: '45 min' }
    ]
  }
};

const ZoneLandingPage = () => {
  const { zone } = useParams();
  const zoneData = zonesData[zone];
  const {seo} = useSeo();

  useEffect(() => {
    if (!zoneData) return;


    const baseUrl = process.env.REACT_APP_SITE_URL;

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Accueil', item: baseUrl },
        { '@type': 'ListItem', position: 2, name: 'Zones de livraison', item: `${baseUrl}/zones-livraison` },
        { '@type': 'ListItem', position: 3, name: zoneData.name, item: `${baseUrl}/location-materiel-zone/${zone}` }
      ]
    };

    const serviceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${baseUrl}/location-materiel-zone/${zone}#service`,
      name: `Location de matériel ${zoneData.name}`,
      provider: {
        '@type': 'LocalBusiness',
        name: '3DLoc',
        telephone: '+33623893298',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Cauville-sur-Mer',
          postalCode: '76930',
          addressCountry: 'FR'
        }
      },
      areaServed: zoneData.communes.map(c => ({
        '@type': 'City',
        name: c.name,
        ...(c.postal && { postalCode: c.postal })
      })),
      description: zoneData.metaDescription
    };

    const schemas = [breadcrumbSchema, serviceSchema];
    const scriptTags = schemas.map((schema, idx) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = `zone-schema-${idx}`;
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
      return script;
    });

    return () => { scriptTags.forEach(s => s.remove()); };
  }, [zone, zoneData]);

  if (!zoneData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-800 mb-4">Zone non trouvée</h1>
          <Link to="/zones-livraison" className="text-green-600 hover:underline">
            Voir toutes nos zones de livraison
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Breadcrumb */}
      <nav aria-label="Fil d'Ariane" className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <ol className="flex items-center gap-2 text-sm text-slate-600 flex-wrap">
            <li><Link to="/" className="hover:text-green-600">Accueil</Link></li>
            <li><ChevronRight size={14} className="text-slate-400" /></li>
            <li><Link to="/zones-livraison" className="hover:text-green-600">Zones de livraison</Link></li>
            <li><ChevronRight size={14} className="text-slate-400" /></li>
            <li className="font-medium text-slate-800" aria-current="page">{zoneData.name}</li>
          </ol>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-green-600 to-green-700 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-green-200 mb-4">
              <MapPin size={18} />
              <span>{zoneData.region} • {zoneData.distance} de notre dépôt • {zoneData.communes.length} communes desservies</span>
            </div>
            <h1 data-testid="zone-heading" className="text-4xl md:text-5xl font-black mb-6 break-words">
              {seo.h1}
            </h1>
            <p className="text-xl text-green-100 mb-8">{zoneData.description}</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 bg-white text-green-600 font-bold rounded-xl hover:bg-green-50 transition-colors">
                Demander un devis gratuit <ArrowRight size={20} />
              </Link>
              <a href="tel:+33623893298" className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 text-white font-bold rounded-xl border-2 border-white hover:bg-green-400 transition-colors">
                <Phone size={20} /> 06 23 89 32 98
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Villes phares + Widget devis */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-slate-800 mb-6">Villes phares de {zoneData.name}</h2>
              <p className="text-slate-600 mb-6">
                Certaines villes de cette zone bénéficient d'une page dédiée avec plus d'informations locales :
              </p>
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {zoneData.featuredCities.map((city) => {
                  const slug = city.toLowerCase()
                    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                    .replace(/['\s]/g, '-');
                  return (
                    <Link
                      key={city}
                      to={`/location-materiel/${slug}`}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-200 hover:border-green-400 hover:bg-green-50 transition-all"
                      data-testid={`zone-featured-${slug}`}
                    >
                      <span className="font-semibold text-slate-800">{city}</span>
                      <ArrowRight className="text-green-600" size={18} />
                    </Link>
                  );
                })}
              </div>
              <p className="text-slate-600">{zoneData.localContent}</p>
            </div>
            <div className="lg:sticky lg:top-6 lg:self-start">
              <QuickQuoteWidget
                defaultCity={zoneData.name}
                title={`Devis en ${zoneData.name}`}
                subtitle="On vous rappelle sous 24h. Pas d'email requis."
              />
            </div>
          </div>
        </div>
      </section>

      {/* Communes desservies - liste SEO complète */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-800 mb-2">
            Toutes les communes desservies en {zoneData.name}
          </h2>
          <p className="text-slate-600 mb-8">
            {zoneData.communes.length} communes avec leur code postal et le temps de livraison depuis notre dépôt.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="zone-communes-list">
            {zoneData.communes.map((commune) => (
              <div
                key={`${commune.name}-${commune.postal}`}
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200"
                data-testid={`zone-commune-${commune.name.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div>
                  <p className="font-medium text-slate-800">{commune.name}</p>
                  {commune.postal && <p className="text-xs text-slate-500">{commune.postal}</p>}
                </div>
                <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                  <Clock size={12} /> {commune.distance}
                </span>
              </div>
            ))}
          </div>
          <p className="text-sm text-slate-500 mt-6">
            Votre commune n'est pas listée ? <a href="tel:+33623893298" className="text-green-600 font-semibold hover:underline">Appelez-nous au 06 23 89 32 98</a> — nous étudions chaque demande.
          </p>
        </div>
      </section>

      {/* CTA final */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Besoin de matériel en {zoneData.name} ?</h2>
          <p className="text-xl text-green-100 mb-8">
            Devis gratuit sous 24h. Livraison 7J/7 sur rendez-vous dans les {zoneData.communes.length} communes.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-green-600 font-bold rounded-xl hover:bg-green-50 transition-colors text-lg">
              Demander un devis gratuit <ArrowRight size={24} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ZoneLandingPage;
